import { useEffect } from "react";

/**
 * Inject one or more JSON-LD structured-data blobs into <head> for SEO.
 * Pass an array (or single object) of schema.org-style objects.
 *
 * Each blob is keyed by `@type` so re-renders replace prior copies cleanly
 * rather than stacking duplicates.
 */
export function useJsonLd(schemas) {
  useEffect(() => {
    if (!schemas) return;
    const list = Array.isArray(schemas) ? schemas : [schemas];

    const added = [];
    list.forEach((schema, idx) => {
      if (!schema) return;
      const id = `jsonld-${schema["@type"] || "doc"}-${idx}`;
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
      added.push(el);
    });

    return () => {
      // Clean up on unmount so route changes don't leak stale schemas
      added.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [schemas]);
}

/* ─── Reusable schema builders ──────────────────────────────────────────── */

const SCHOOL_NAME = "APM Fire Alarm Exam Prep";
const SCHOOL_URL = "https://apmfirealarm.com";
const SCHOOL_LOGO = `${SCHOOL_URL}/og-image.png`;

export function schoolSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "@id": `${SCHOOL_URL}/#organization`,
    name: SCHOOL_NAME,
    url: SCHOOL_URL,
    logo: SCHOOL_LOGO,
    description:
      "Online exam prep preparing students for the NICET Level 1 Foundation exam and NYC FDNY Certificates of Fitness (S-95, S-98, S-12, S-13, F-53). 140 hours of curriculum + 7 specialty tracks.",
    foundingDate: "2026-01",
    foundingLocation: {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: "New York", addressRegion: "NY", addressCountry: "US" },
    },
    sameAs: [],
    member: { "@type": "Person", name: "Trevor Marson" },
  };
}

export function instructorSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SCHOOL_URL}/about-instructor#trevor`,
    name: "Trevor Marson",
    jobTitle: "Lead Instructor & Founder, APM Fire Alarm Exam Prep",
    description:
      "15+ years of NYC fire-alarm field experience at World Trade Center, Hudson Yards, JPMorgan Chase, Federal Reserve Bank of NY, NYU, John Jay College, JFK Airport, and Walmart Northeast. FDNY S-95, S-98, S-12, S-13, F-53, OSHA 30, IBEW Local 3 H-Division.",
    image: `${SCHOOL_URL}/og-image.png`,
    url: `${SCHOOL_URL}/about-instructor`,
    worksFor: { "@id": `${SCHOOL_URL}/#organization` },
    knowsAbout: [
      "Fire Alarm Systems",
      "NFPA 72",
      "UL 864",
      "NEC Article 760",
      "FDNY Certificates of Fitness",
      "NICET Level 1 Foundation",
      "NYC Fire Code",
    ],
    hasCredential: [
      { "@type": "EducationalOccupationalCredential", name: "FDNY S-95 Certificate of Fitness" },
      { "@type": "EducationalOccupationalCredential", name: "FDNY S-98 Certificate of Fitness" },
      { "@type": "EducationalOccupationalCredential", name: "FDNY S-12 Citywide Sprinkler Systems" },
      { "@type": "EducationalOccupationalCredential", name: "FDNY S-13 Citywide Standpipe Systems" },
      { "@type": "EducationalOccupationalCredential", name: "FDNY F-53 Fire Alarm w/ One-Way Voice" },
      { "@type": "EducationalOccupationalCredential", name: "OSHA 30 Construction" },
      { "@type": "EducationalOccupationalCredential", name: "IBEW Local 3 H-Division" },
    ],
  };
}

export function courseSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${SCHOOL_URL}/#course`,
    name: "NICET Level 1 Foundation + NYC FDNY S-98 — Full 140-Hour Curriculum",
    description:
      "140 hours of interactive fire alarm training. NICET Level 1 Foundation + Level 2 + Level 3 + NYC FDNY S-98, AI tutor, interactive lab simulators (FACP panel + riser diagrams), 7 specialty tracks (S-95, S-12, S-13, F-53, BMS, Voice/EVACS, Manufacturer), QR-verified printable certificate.",
    provider: { "@id": `${SCHOOL_URL}/#organization` },
    instructor: { "@id": `${SCHOOL_URL}/about-instructor#trevor` },
    educationalLevel: "Beginner to Intermediate",
    courseMode: ["online", "asynchronous"],
    inLanguage: "en-US",
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
      courseWorkload: "PT140H",
    },
    offers: {
      "@type": "Offer",
      price: "149.00",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: `${SCHOOL_URL}/enroll`,
      validFrom: "2026-01-01",
    },
    aggregateRating: undefined,  // can be filled when you collect real reviews
  };
}

export function breadcrumbSchema(items) {
  // items: [{name: 'Home', url: '/'}, {name: 'Courses', url: '/curriculum'}, ...]
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SCHOOL_URL}${it.url}`,
    })),
  };
}

export function faqSchema(faqs) {
  // faqs: [{q: 'Question?', a: 'Answer.'}, ...]
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export function specialtyCourseSchema(track) {
  // track: {id, short_name, name, hours, description, credential, color}
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${SCHOOL_URL}/track/${track.id}#course`,
    name: track.name,
    description: track.description,
    provider: { "@id": `${SCHOOL_URL}/#organization` },
    instructor: { "@id": `${SCHOOL_URL}/about-instructor#trevor` },
    educationalLevel: "Specialty",
    courseMode: ["online", "asynchronous"],
    inLanguage: "en-US",
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
      courseWorkload: `PT${track.hours}H`,
    },
    offers: {
      "@type": "Offer",
      price: "0.00",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      description: "Included free with $149 enrollment",
    },
  };
}
